import { TrendingUp, TrendingDown } from 'lucide-react';
import { STATISTIC_LABELS, BASELINE_DAYS } from './constants';
import { helpText, Help } from './help';
import { formatDate, formatMetric, formatRange, displayDeviceModel, formatScenarioLabel, listPhrases } from './format';
import { isMissing, verdictOf } from './metrics';

function devicesLine(devices, kpi, onlyOfMany) {
  const models = devices.map(displayDeviceModel).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  if (!models.length) return null;
  if (models.length === 1) return `Device: ${models[0]}${onlyOfMany ? ' only' : ''}`;
  return `Devices: ${models.join(' + ')} (${kpi.aggregate === 'highest' ? 'highest' : 'average'})`;
}

const RUN_STATUS = {
  success: { text: 'Completed', tone: 'success' },
  failure: { text: 'Some tests failed', tone: 'failure', note: 'Figures may be incomplete' },
  cancelled: { text: 'Cancelled', tone: 'failure', note: 'Figures may be incomplete' },
  in_progress: { text: 'In progress', tone: 'in-progress' },
  queued: { text: 'Queued', tone: 'in-progress' },
};

function runStatus(conclusion) {
  return RUN_STATUS[conclusion] || { text: conclusion || 'Unknown', tone: 'neutral' };
}

export function StatusBadge({ conclusion }) {
  const status = runStatus(conclusion);
  return (
    <>
      <span className={`sdkp-status sdkp-status-${status.tone}`}>{status.text}</span>
      {status.note && <span className="sdkp-status-note">{status.note}</span>}
    </>
  );
}

function SnapshotValue({ value, unit, signed, conclusion, notMeasured }) {
  if (!isMissing(value)) return formatMetric(value, unit, signed);
  if (notMeasured) return <span className="sdkp-not-measured">{notMeasured}</span>;
  if (conclusion !== 'failure' && conclusion !== 'cancelled') return '-';
  return (
    <span
      className="sdkp-snapshot-warning"
      title={`This value is missing and the run ended in ${conclusion}. Check the run to see whether the two are related.`}
    >
      ⚠ -
    </span>
  );
}

function TrendMark({ above }) {
  const Icon = above ? TrendingUp : TrendingDown;
  return <Icon className="sdkp-trend-mark" size={14} aria-hidden="true" />;
}

function ChangeLine({ value, range, unit }) {
  if (isMissing(value)) return null;
  if (!range) return <p className="sdkp-card-change sdkp-delta-flat">Too few runs on these devices for a usual range</p>;
  const average = `7-day avg ${formatMetric(range.mean, unit)}`;
  if (isMissing(range.low)) {
    return <p className="sdkp-card-change sdkp-delta-flat">{average} · too few runs on these devices for a usual range</p>;
  }
  const verdict = verdictOf(value, range);
  const rangeText = `usual range ${formatRange(range, unit)}`;
  if (verdict === 'steady') {
    return <p className="sdkp-card-change sdkp-delta-flat">≈ Steady · {average} ({rangeText})</p>;
  }
  const above = verdict === 'above';
  return (
    <p className={`sdkp-card-change ${above ? 'sdkp-delta-worse' : 'sdkp-delta-better'}`}>
      <TrendMark above={above} />
      {above ? 'Above' : 'Below'} usual range · {average} ({rangeText})
    </p>
  );
}

// The change since the previous release, judged by the release verdict.
function ReleaseChangeLine({ value, previous, verdict, unit, signed }) {
  if (isMissing(value)) return null;
  if (!previous.comparable) {
    return <p className="sdkp-card-change sdkp-delta-flat">{`Measured on other devices in ${previous.label}; pick one device to compare.`}</p>;
  }
  if (isMissing(previous.value)) {
    return <p className="sdkp-card-change sdkp-delta-flat">{`No ${previous.label} figure to compare with.`}</p>;
  }
  const change = value - previous.value;
  const percent = previous.value ? ` (${change > 0 ? '+' : ''}${Math.round((change / Math.abs(previous.value)) * 100)} %)` : '';
  const tone = { above: 'sdkp-delta-worse', below: 'sdkp-delta-better' }[verdict] || 'sdkp-delta-flat';
  return (
    <p className={`sdkp-card-change ${tone}`}>
      {(verdict === 'above' || verdict === 'below') && <TrendMark above={verdict === 'above'} />}
      {verdict === 'steady' ? '≈ Steady · ' : ''}
      {`${formatMetric(change, unit, true)}${percent} vs ${formatMetric(previous.value, unit, signed)} in ${previous.label}`}
    </p>
  );
}

function CostNote({ kpi, figure, platform }) {
  if (kpi.key === 'startup') {
    return <p className="sdkp-card-note">Median startup time on each device, in the test where it was slowest.</p>;
  }
  if (!kpi.metric) {
    return (
      <p className="sdkp-card-note">
        We record at 3 quality levels (how sharp each screenshot is and how many are taken per second) and show the one that used the most data. Masking is turned off, so every text and image on screen is recorded and uploaded.
      </p>
    );
  }
  if (!figure) return null;
  const statisticLabel = STATISTIC_LABELS[platform][kpi.metric];
  const { unit, label } = kpi.raw;
  const where = figure.deviceCount > 1
    ? ", at each device's heaviest scenario"
    : (formatScenarioLabel(figure.scenario) ? `, in its heaviest scenario (${formatScenarioLabel(figure.scenario)})` : '');
  const withSdk = `with SDK ${formatMetric(figure.enabled, unit)}`;
  const withoutSdk = `without SDK ${formatMetric(figure.disabled, unit)}`;
  let body;
  if (isMissing(figure.enabled)) {
    body = `${label}: SDK started minus SDK never started${where}.`;
  } else if (isMissing(figure.disabled)) {
    body = `${label} ${withSdk}. No without-SDK run to compare with.`;
  } else {
    body = kpi.metric === 'fps' ? `${label} ${withoutSdk} − ${withSdk}${where}.` : `${label} ${withSdk} − ${withoutSdk}${where}.`;
    if (figure.sharedBaseline) body += ' The without-SDK app runs only once a night, so each run is compared with that one.';
  }
  return (
    <p className="sdkp-card-note">
      {statisticLabel}
      <Help text={helpText('statistic', platform)} align="left" />
      {' '}
      {body}
    </p>
  );
}

// `previous` ({ value, label, comparable }) and `verdict` switch the card to a
// comparison with the previous release; `noComparison` shows the value alone.
export function KpiCard({
  kpi, platform, value, range, previous, verdict, noComparison, figure, devices, onlyOfMany, conclusion, notMeasured, active,
}) {
  const unit = kpi.unit;
  return (
    <div className={`sdkp-card sdkp-kpi ${active ? 'sdkp-card-active' : ''}`} data-kpi={kpi.key}>
      <p className="sdkp-card-label">{kpi.title}</p>
      <p className="sdkp-card-value">
        <SnapshotValue value={value} unit={unit} signed={kpi.signed} conclusion={conclusion} notMeasured={notMeasured} />
      </p>
      {previous && <ReleaseChangeLine value={value} previous={previous} verdict={verdict} unit={unit} signed={kpi.signed} />}
      {!previous && !noComparison && <ChangeLine value={value} range={range} unit={unit} />}
      {kpi.key === 'upload' && !isMissing(value) && (
        <p className="sdkp-card-meaning">{`About ${Math.round((value * 60) / 1000)} MB of mobile data per hour of app use while Session Replay records.`}</p>
      )}
      <div className="sdkp-card-tech">
        <CostNote kpi={kpi} figure={figure} platform={platform} />
        {devices && <p className="sdkp-card-source">{devicesLine(devices, kpi, onlyOfMany)}</p>}
      </div>
    </div>
  );
}

// One sentence on whether the SDK's costs moved against their usual range or,
// with `previousLabel`, against the previous release.
export function Takeaway({ rows, platform, previousLabel }) {
  const judged = rows.filter((row) => row.verdict);
  if (!rows.some((row) => !isMissing(row.value))) return null;
  if (!judged.length) {
    return (
      <div className="sdkp-takeaway">
        {previousLabel
          ? `Not enough comparable figures in ${previousLabel} to say whether the SDK's costs moved.`
          : `Not enough runs in the previous ${BASELINE_DAYS} days to say whether the SDK's costs moved.`}
      </div>
    );
  }
  let rangeHelpShown = false;
  const rangeHelp = () => {
    if (rangeHelpShown) return null;
    rangeHelpShown = true;
    return <Help text={helpText('range', platform)} />;
  };
  const phrase = (row) => (
    <>
      <b>{row.kpi.phrase}</b>
      <Help text={helpText(row.kpi.help, platform)} />
    </>
  );
  const steady = judged.filter((row) => row.verdict === 'steady');
  const moved = judged.filter((row) => row.verdict !== 'steady');
  return (
    <div className="sdkp-takeaway">
      {previousLabel ? `Compared with the previous release ${previousLabel}, ` : `Compared with the previous ${BASELINE_DAYS} days, `}
      {steady.length > 0 && (
        <>
          {"the SDK's "}
          {listPhrases(steady.map((row) => ({ key: row.kpi.key, node: phrase(row) })))}
          {steady.length === 1 ? ' is steady.' : ` are ${steady.length === rows.length ? 'all ' : ''}steady.`}
        </>
      )}
      {moved.map((row, index) => {
        const above = row.verdict === 'above';
        const lead = steady.length === 0 && index === 0 ? "the SDK's " : ' Its ';
        const verb = above ? 'rose' : 'fell';
        if (previousLabel) {
          return (
            <span key={row.kpi.key} className={above ? 'sdkp-delta-worse' : 'sdkp-delta-better'}>
              {lead}
              {phrase(row)}
              {` ${verb} to ${formatMetric(row.value, row.kpi.unit, row.kpi.signed)}, from ${formatMetric(row.previous.value, row.kpi.unit, row.kpi.signed)} in ${previousLabel}.`}
            </span>
          );
        }
        return (
          <span key={row.kpi.key} className={above ? 'sdkp-delta-worse' : 'sdkp-delta-better'}>
            {lead}
            {phrase(row)}
            {` ${verb} to ${formatMetric(row.value, row.kpi.unit, row.kpi.signed)}, ${above ? 'above' : 'below'} its usual range`}
            {rangeHelp()}
            {` (${formatRange(row.range, row.kpi.unit)}).`}
          </span>
        );
      })}
    </div>
  );
}

export function RunContext({ run, summary, deviceCount, label = 'Latest run' }) {
  if (!run) return null;
  const status = runStatus(run.conclusion);
  return (
    <div className="sdkp-context">
      <span>{label}: <b>{formatDate(run.uploadedAt)}</b></span>
      <span className={`sdkp-context-status sdkp-context-${status.tone}`}>
        {status.tone === 'success' ? '✓ ' : ''}
        {status.text}
        {status.note ? ` — ${status.note.toLowerCase()}` : ''}
      </span>
      {summary?.sdkVersion && <span>SDK <b>{summary.sdkVersion}</b></span>}
      {summary?.appVersion && <span>Test app <b>{summary.appVersion}</b></span>}
      <span>Measured on <b>{deviceCount} device{deviceCount === 1 ? '' : 's'}</b></span>
      {run.url && <a href={run.url} target="_blank" rel="noopener noreferrer">Open run on GitHub ↗</a>}
    </div>
  );
}
